import React from 'react'
import { toDate } from '@hikakaku/shared/util/date_util'

export const RANKING_STORES_PER_PAGE = 10
export const INITIAL_SHOW_COUNT = 3
export const NOIMAGE_PATH = '/wp-content/themes/hikakaku_theme/images/company_noimage.jpg'
export const PURCHASE_METHODS = ['宅配', '店頭', '出張']

export function spRankLabel(rank) {
  return String(rank)
}

export function storePath(store) {
  if (!store || !store.id) return '#'
  return `/company/${store.id}/`
}

function kaitoriHistories(entry) {
  return entry.kaitori_histories || entry.kaitoriHistories || []
}

function historyBuyPrice(history) {
  if (!history) return null

  const info = history.info || {}
  const raw = history.buy_price ?? history.buyPrice ?? info.buy_price ?? info.buyPrice
  if (raw != null && raw !== '') {
    const amount = Number(raw)
    return Number.isFinite(amount) && amount > 0 ? amount : null
  }

  const formatted = history.price ?? info.price
  if (!formatted || formatted === '価格なし') return null

  const amount = Number(String(formatted).replace(/[^\d]/g, ''))
  return amount > 0 ? amount : null
}

export function bestKaitoriHistory(entry) {
  const histories = kaitoriHistories(entry)
  if (!histories.length) return null

  return histories.reduce((best, current) => {
    const currentPrice = historyBuyPrice(current)
    const bestPrice = historyBuyPrice(best)
    if (currentPrice != null && (bestPrice == null || currentPrice > bestPrice)) return current
    return best
  })
}

function formatDateLine(value, suffix) {
  if (value == null || value === '') return null
  const date = toDate(value)
  if (!date) return null
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `◆ ${date.getFullYear()}/${month}/${day} ${suffix}`
}

export function priceDateLabel(entry, history) {
  const info = history?.info || {}
  const buyDateIn = history?.buy_date_in || history?.buyDateIn || info.buy_date_in || info.buyDateIn
  if (history && buyDateIn) {
    return formatDateLine(buyDateIn, '査定実績')
  }

  const createdAt = entry.price_created_at || entry.priceCreatedAt
  if (entry.price > 1 && createdAt) {
    return formatDateLine(createdAt, '査定実績')
  }

  return null
}

export function displayPrice(entry, history) {
  const achievementPrice = historyBuyPrice(history)
  if (achievementPrice != null) return `¥${achievementPrice.toLocaleString()}`

  if (entry.price && entry.price > 1) return `¥${entry.price.toLocaleString()}`

  return '要見積もり'
}

export function displayPricePc(entry, history) {
  const achievementPrice = historyBuyPrice(history)
  if (achievementPrice != null) return `¥${achievementPrice.toLocaleString()}`

  if (entry.price && entry.price > 1) return `¥${entry.price.toLocaleString()} 前後`

  return '要見積もり'
}

export function conditionLabel(history) {
  if (history && history.condition) return history.condition
  return null
}

/** SP card: one meta line below price — e.g. 査定日：2026/5/13 新品未開封 */
export function spPriceMetaLabel(entry, history) {
  const tail = history?.condition || ''

  if (history && (history.buy_date_in || history.buyDateIn)) {
    const date = toDate(history.buy_date_in || history.buyDateIn)
    if (date) {
      const dateLine = `査定日：${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
      return tail ? `${dateLine} ${tail}` : dateLine
    }
  }

  const createdAt = entry.price_created_at || entry.priceCreatedAt
  if (entry.price > 1 && createdAt) {
    const date = toDate(createdAt)
    if (date) {
      const dateLine = `査定日：${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
      return tail ? `${dateLine} ${tail}` : dateLine
    }
  }

  return null
}

export function conditionLabelPc(entry, history) {
  const price = displayPricePc(entry, history)
  if (price === '要見積もり') return null
  return conditionLabel(history)
}

export function stars(rating) {
  const value = Math.min(5, Math.max(0, rating || 0))
  const widthPercent = (value / 5) * 100

  return (
    <span style={{ position: 'relative', display: 'inline-block', whiteSpace: 'nowrap' }}>
      <span>☆☆☆☆☆</span>
      <span style={{
        position: 'absolute',
        left: 0,
        top: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: `${widthPercent}%`
      }}>★★★★★</span>
    </span>
  )
}

export function formatRating(rating) {
  return (Number(rating) || 0).toFixed(2)
}

export function storeField(store, ...keys) {
  if (!store) return ''
  for (const key of keys) {
    const value = store[key]
    if (value != null && value !== false && value !== '') return value
  }
  return ''
}

export function shouldShowPhone(store) {
  const isPhoneOpen = storeField(store, 'is_phone_open', 'isPhoneOpen')
  if (!isPhoneOpen) {
    return false
  }

  return Boolean(storeField(store, 'phone_number', 'phoneNumber'))
}

export function callHours(store) {
  const open = storeField(store, 'phone_open_time', 'phoneOpenTime')
  const close = storeField(store, 'phone_close_time', 'phoneCloseTime')
  if (!open && !close) return ''
  return `受付時間：${open}～${close}`
}

export function hasRecentAssessment(entry) {
  return kaitoriHistories(entry).length > 0
}

export function freeShippingTag(store) {
  const postage = storeField(store, 'postage')
  if (!postage) return null
  return postage.includes('無料') ? '送料無料' : null
}

export function isCertifiedStore(store) {
  if (!store) return false

  const activeAssessmentFormProduct =
    store.active_assessment_form_product ?? store.activeAssessmentFormProduct
  if (activeAssessmentFormProduct === true || activeAssessmentFormProduct === 1) {
    return true
  }

  const contract = store.store_contract || store.storeContract || {}
  return Boolean(contract.assessment_form || contract.assessmentForm)
}
